import type { ReportColumn } from '../models/column.types'
import type {
  PreparedReportExport,
  ReportExportColumn,
  ReportExportCommand,
  ReportExportContext,
  ReportExportOutcome,
  ReportRemoteExportResult,
} from '../models/export.types'
import { ASYNC_EXPORT_CELL_THRESHOLD } from '../models/export.types'
import type { ReportResponse, ReportSpec } from '../models/report.types'

import { buildComparisonRows } from './comparisonColumns'
import { normalizeReportTableRows } from './reportMediaPresentation'
import { resolveReportRequest } from './reportRequest'

export interface ReportExportTransport {
  requestExport: (
    url: string,
    query: string,
    signal?: AbortSignal,
  ) => Promise<ReportRemoteExportResult>
  fetchReport?: (url: string, query: string, signal?: AbortSignal) => Promise<ReportResponse>
}

export interface ReportXlsxWriter {
  write: (prepared: PreparedReportExport) => Promise<string>
}

export type ReportExportMode = 'sheet' | 'local_xlsx' | 'async_xlsx'

export class ReportExportIncompleteError extends Error {
  readonly code = 'incomplete_rows'

  constructor() {
    super('incomplete_rows')
    this.name = 'ReportExportIncompleteError'
  }
}

export class ReportExportUnsupportedPresentationError extends Error {
  readonly code = 'unsupported_presentation'

  constructor() {
    super('unsupported_presentation')
    this.name = 'ReportExportUnsupportedPresentationError'
  }
}

export function estimateExportCells(rowCount: number, columnCount: number): number {
  const rows = Number.isFinite(rowCount) ? Math.max(0, Math.floor(rowCount)) : 0
  const columns = Number.isFinite(columnCount) ? Math.max(0, Math.floor(columnCount)) : 0
  return rows * columns
}

function toExportColumn(column: ReportColumn): ReportExportColumn {
  const comparison = column.meta?.['reportComparison']
  const comparisonBaseField =
    typeof comparison === 'object' &&
    comparison !== null &&
    typeof (comparison as Record<string, unknown>)['baseField'] === 'string'
      ? ((comparison as Record<string, unknown>)['baseField'] as string)
      : undefined
  return {
    field: column.field,
    header: column.header,
    ...(column.numeric === undefined ? {} : { numeric: column.numeric }),
    ...(column.sub_type === undefined ? {} : { sub_type: column.sub_type }),
    ...(column.currency === undefined ? {} : { currency: column.currency }),
    ...(column.percentage === undefined ? {} : { percentage: column.percentage }),
    ...(comparisonBaseField ? { renderOnly: true, comparisonBaseField } : {}),
  }
}

function isVisible(column: ReportColumn): boolean {
  // DataVisible's presentation layer owns visibility through `invisible`; legacy catalog
  // `selected` flags must not remove a column that the active table/chart is rendering.
  return !column.invisible
}

/** Prepare the current visible, ordered grid without mutating row or column ownership. */
export function prepareReportExport(command: ReportExportCommand): PreparedReportExport {
  const seen = new Set<string>()
  const columns = command.columns.flatMap((column) => {
    if (!column.field || !isVisible(column) || seen.has(column.field)) return []
    seen.add(column.field)
    return [toExportColumn(column)]
  })
  return { name: command.name.trim() || 'report', rows: [...command.rows], columns }
}

export function resolveReportExportMode(command: ReportExportCommand): ReportExportMode {
  if (command.action === 'sheet') return 'sheet'
  if (command.remotePresentationCompatible === false) return 'local_xlsx'
  const prepared = prepareReportExport(command)
  const cells = estimateExportCells(
    command.totalRowCount ?? prepared.rows.length,
    prepared.columns.length,
  )
  return command.asyncEligible && cells >= ASYNC_EXPORT_CELL_THRESHOLD ? 'async_xlsx' : 'local_xlsx'
}

function exportSpec(
  command: ReportExportCommand,
  prepared: PreparedReportExport,
  mode: Exclude<ReportExportMode, 'local_xlsx'>,
): ReportSpec {
  const requestedDimensions = new Set(command.spec.dimensions ?? [])
  const requestedMetrics = new Set(command.spec.metrics ?? [])
  const dimensions: string[] = []
  const metrics: string[] = []
  for (const column of prepared.columns) {
    if (column.renderOnly) continue
    if (requestedMetrics.has(column.field)) metrics.push(column.field)
    else if (requestedDimensions.has(column.field)) dimensions.push(column.field)
  }
  const labels = new Map(prepared.columns.map((column) => [column.field, column.header]))
  const names = (fields: readonly string[]): Array<{ field: string; name: string }> =>
    fields.map((field) => ({ field, name: labels.get(field) ?? field }))

  const spec: ReportSpec = {
    ...command.spec,
    dimensions,
    metrics,
    pagination_enabled: false,
    export_format: mode === 'sheet' ? 'sheets' : 'async_xlsx',
    ...(command.recipients?.length ? { export_email: command.recipients.join(',') } : {}),
    ...(mode === 'sheet' && command.shareWith?.length
      ? { share_with: [...command.shareWith] }
      : {}),
    export_dimension_key_names: names(dimensions),
    export_metric_key_names: names(metrics),
  }
  delete spec.first
  return spec
}

export async function executeReportExport(
  command: ReportExportCommand,
  context: ReportExportContext,
  transport: ReportExportTransport,
  writer: ReportXlsxWriter,
  signal?: AbortSignal,
): Promise<ReportExportOutcome> {
  let prepared = prepareReportExport(command)
  const mode = resolveReportExportMode(command)
  if (mode === 'sheet' && command.remotePresentationCompatible === false) {
    throw new ReportExportUnsupportedPresentationError()
  }
  if (mode === 'local_xlsx') {
    const totalRows = command.totalRowCount ?? prepared.rows.length
    if (totalRows > prepared.rows.length) {
      if (!transport.fetchReport) throw new TypeError('Full report export transport is required')
      const fullSpec: ReportSpec = { ...command.spec, pagination_enabled: false }
      delete fullSpec.first
      delete fullSpec.export_format
      delete fullSpec.export_email
      delete fullSpec.share_with
      const request = resolveReportRequest(fullSpec, { ...context, exportRequest: true })
      const response = await transport.fetchReport(request.url, request.query, signal)
      if (response.data.length < totalRows) throw new ReportExportIncompleteError()
      const normalized = normalizeReportTableRows(response.data, command.spec.report_id)
      const comparisonFields = [
        ...new Set(
          prepared.columns.flatMap((column) =>
            column.comparisonBaseField ? [column.comparisonBaseField] : [],
          ),
        ),
      ]
      if (command.presentFetchedPresentation) {
        const presentation = command.presentFetchedPresentation(response.data)
        prepared = prepareReportExport({
          ...command,
          rows: presentation.rows,
          columns: presentation.columns,
        })
      } else {
        prepared = {
          ...prepared,
          rows:
            comparisonFields.length > 0
              ? buildComparisonRows(normalized, comparisonFields)
              : normalized,
        }
      }
    }
    const filename = await writer.write(prepared)
    return { kind: 'xlsx', filename }
  }

  const spec = exportSpec(command, prepared, mode)
  const request = resolveReportRequest(spec, { ...context, exportRequest: true })
  return transport.requestExport(request.url, request.query, signal)
}
